import { Link, useParams } from "react-router-dom";
import { useMemo } from "react";
import { ArrowRight, ArrowUpRight, Check, FileText } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { PRODUCTS, getProduct } from "@/lib/products";
import { CARD_CATEGORIES } from "@/lib/cardCategories";
import { inrShort } from "@/lib/format";

export default function ProductPage() {
  const { slug } = useParams();
  const product = getProduct(slug);

  const faqSchema = useMemo(
    () =>
      product
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: product.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null,
    [product]
  );

  useSEO({
    title: product ? `${product.name} — Indicative Rates, Eligibility & Apply | Loan Salaah` : "Loan not found | Loan Salaah",
    description: product ? `${product.short} Indicative rates from ${product.rateMin}% p.a. Check eligibility, documents and apply through Loan Salaah.` : "",
    jsonLd: faqSchema,
  });

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-40 text-center" data-testid="product-not-found">
        <h1 className="font-serif text-4xl font-semibold text-concordia">This loan product doesn't exist.</h1>
        <Link to="/" className="mt-6 inline-block rounded-full bg-concordia px-7 py-3 text-sm font-semibold text-ivory">Back to home</Link>
      </div>
    );
  }

  const others = PRODUCTS.filter((p) => p.slug !== product.slug);

  return (
    <div data-testid={`product-page-${product.slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden pb-16 pt-36 sm:pb-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-4">
                <span className="brass-rule" aria-hidden="true" />
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Loan product</span>
              </div>
              <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl lg:text-6xl">
                {product.name}
              </h1>
              <p className="mt-4 font-serif text-xl italic text-slate sm:text-2xl">{product.tagline}</p>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/80">{product.description[0]}</p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to={`/apply?product=${product.slug}`} data-testid="product-apply-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-concordia px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(30,74,61,0.35)]">
                  Apply for {product.name}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                {product.inCalculators !== false ? (
                  <Link to="/emi-calculator" data-testid="product-calculator-link"
                    className="inline-flex items-center gap-2 rounded-full border border-concordia/25 px-7 py-3.5 text-sm font-semibold text-concordia transition-colors hover:border-brass hover:text-brass-deep">
                    Calculate EMI
                  </Link>
                ) : (
                  <Link to="/contact" data-testid="product-calculator-link"
                    className="inline-flex items-center gap-2 rounded-full border border-concordia/25 px-7 py-3.5 text-sm font-semibold text-concordia transition-colors hover:border-brass hover:text-brass-deep">
                    Ask about cards
                  </Link>
                )}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -right-3 -top-3 h-full w-full rounded-b-[1.5rem] rounded-t-[7rem] border border-brass/50" aria-hidden="true" />
              <img src={product.image} alt={`${product.name} — ${product.tagline}`} data-testid="product-hero-image"
                className="relative aspect-[4/3] w-full rounded-b-[1.5rem] rounded-t-[7rem] object-cover shadow-[0_30px_70px_rgba(30,74,61,0.2)]" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-sand bg-cream">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-sand px-4 sm:grid-cols-3 sm:divide-x sm:divide-y-0 sm:px-6 lg:px-8" data-testid="product-stats">
          {(product.customStats || [
            { label: "Indicative rate", value: `${product.rateMin}% – ${product.rateMax}% p.a.*` },
            { label: "Amounts up to", value: inrShort(product.amountMax) },
            { label: "Tenures up to", value: `${Math.round(product.tenureMax / 12)} years` },
          ]).map((s) => (
            <div key={s.label} className="py-6 sm:px-8 sm:py-8">
              <p className="font-mono text-[0.62rem] uppercase tracking-[0.25em] text-slate">{s.label}</p>
              <p className="mt-2 font-serif text-2xl font-semibold text-concordia">{s.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Overview + highlights */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl">How it works with Loan Salaah</h2>
            <p className="mt-6 text-base leading-relaxed text-charcoal/80">{product.description[1]}</p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="product-highlights">
              {product.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 rounded-2xl border border-sand bg-white p-4 text-sm text-charcoal/85">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brass" />
                  {h}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="rounded-3xl bg-concordia-ink p-8 text-ivory" data-testid="product-eligibility-card">
              <h3 className="font-serif text-2xl font-semibold">Eligibility, broadly</h3>
              <ul className="mt-5 space-y-3">
                {product.eligibility.map((e) => (
                  <li key={e} className="flex items-start gap-3 text-sm leading-relaxed text-ivory/75">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-brass" aria-hidden="true" />
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Documents + FAQ */}
      <section className="bg-cream py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <Reveal className="lg:col-span-5">
            <div className="rounded-3xl border border-sand bg-white p-8" data-testid="product-documents-card">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-brass" />
                <h3 className="font-serif text-2xl font-semibold text-concordia">Documents you'll need</h3>
              </div>
              <ul className="mt-5 space-y-3">
                {product.documents.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm leading-relaxed text-charcoal/80">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <h3 className="font-serif text-2xl font-semibold text-concordia sm:text-3xl">Frequently asked</h3>
            <Accordion type="single" collapsible className="mt-6 divide-y divide-sand rounded-3xl border border-sand bg-white px-6" data-testid="product-faq">
              {product.faqs.map((f, i) => (
                <AccordionItem key={i} value={`p-faq-${i}`} className="border-none">
                  <AccordionTrigger data-testid={`product-faq-question-${i}`} className="py-5 text-left text-base font-semibold text-concordia hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-slate">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Card category sub-pages */}
      {product.slug === "credit-cards" && (
        <section className="py-16 sm:py-24" data-testid="card-categories-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="flex items-center gap-4">
                <span className="brass-rule" aria-hidden="true" />
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Browse by category</span>
              </div>
              <h2 className="mt-5 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-concordia sm:text-4xl">
                Which card family fits your spending?
              </h2>
            </Reveal>
            <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {CARD_CATEGORIES.map((c) => (
                <StaggerItem key={c.slug}>
                  <Link
                    to={`/loans/credit-cards/${c.slug}`}
                    data-testid={`card-category-card-${c.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-sand bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(20,40,75,0.14)]"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={c.image} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-serif text-xl font-semibold text-concordia">{c.name.replace(" Credit Cards", "")}</h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate">{c.short}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-concordia">
                        Explore
                        <ArrowUpRight className="h-4 w-4 text-brass transition-transform duration-300 group-hover:rotate-45" />
                      </span>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* Other products + CTA */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h3 className="font-serif text-2xl font-semibold text-concordia">Explore other loans</h3>
            <div className="mt-6 flex flex-wrap gap-3" data-testid="product-related">
              {others.map((p) => (
                <Link key={p.slug} to={`/loans/${p.slug}`} data-testid={`related-${p.slug}`}
                  className="group inline-flex items-center gap-2 rounded-full border border-sand bg-white px-5 py-2.5 text-sm font-medium text-charcoal/80 transition-all duration-300 hover:border-concordia hover:text-concordia">
                  {p.name}
                  <ArrowUpRight className="h-3.5 w-3.5 text-brass transition-transform duration-300 group-hover:rotate-45" />
                </Link>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative mt-14 overflow-hidden rounded-[2rem] bg-concordia p-10 text-ivory sm:p-12" data-testid="product-cta-banner">
              <div className="noise-overlay" aria-hidden="true" />
              <div className="relative flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
                <div>
                  <h3 className="font-serif text-2xl font-semibold sm:text-3xl">Ready when you are.</h3>
                  <p className="mt-2 max-w-md text-sm text-ivory/70">Send an enquiry — an advisor compares partner offers and calls you back. Free for borrowers.</p>
                </div>
                <Link to={`/apply?product=${product.slug}`} data-testid="product-bottom-apply-button"
                  className="rounded-full bg-ivory px-7 py-3.5 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,0,0,0.3)]">
                  Apply Now
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
